import { utcOffset } from '@dispatch/shared';
import {
  ResourceHistoryEntry,
  ResourceHistoryFieldChange,
} from '@dispatch/shared/types';
import {
  FieldChangeGroup,
  formatPathWithinItem,
  groupFieldChanges,
} from './fieldChangeGroups';
import { formatFieldPath } from './fieldLabels';
import { formatChangeCells, MemberNameResolver } from './membershipValues';

/** Auto-managed timestamp fields suppressed in the at-a-glance grid summary. */
export const SYSTEM_FIELD_SUFFIXES = ['CreatedAt', 'UpdatedAt'];

/**
 * Low-signal or oversized LEAF fields suppressed from the grid summary only.
 * Geocode coordinates and a location's per-day availability flags dominate the
 * one-line detail on any location change without telling the reader much, and its
 * inclusion/exclusion zone lists are simply too long for one line. The modal still
 * shows every one of them, and the audit record itself is untouched.
 *
 * Matched on the FINAL path segment, not as a suffix: `ClaimOwnerCapacity.OnMonday`
 * and `FieldCapacityByDay.InitialOnMonday` both END with "Monday" but are real
 * capacity numbers that must stay in the summary. Only a bare `….Monday` leaf
 * (a location's availability boolean) is noise.
 */
/**
 * Leaf fields whose value is a separator-joined list of atomic entries rather than one
 * scalar — a location's inclusion/exclusion territory.
 *
 * Two surfaces need this, which is why it is one exported constant rather than two
 * literals: the grid summary suppresses them (below), and the detail modal only claims an
 * entry count for a value it knows to be a list. Splitting them risked the two drifting.
 *
 * Safe as leaf matches: a location's own address fields are the SINGULAR `State`, `County`
 * and `Zip`, which are distinct leaves. Only `ZoneInfo` emits these plurals.
 */
export const ZONE_LIST_LEAF_FIELDS = ['States', 'Counties', 'ZipCodes'];

export const SUMMARY_NOISE_LEAF_FIELDS = [
  'Lat',
  'Long',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
  // The longest values the audit carries — a single row can run to thousands of characters —
  // so left in the one-line summary they bury every other change on the location. The modal
  // shows them in full behind an expand control.
  ...ZONE_LIST_LEAF_FIELDS,
];

/** True when a field path's leaf holds a separator-joined list of entries. */
export function isZoneListField(fieldPath: string): boolean {
  return ZONE_LIST_LEAF_FIELDS.includes(fieldPath.split('.').pop() ?? '');
}

/**
 * Field path suffix carrying a certification's catalog key. Unlike the fields
 * above, this one is hidden EVERYWHERE — summary and modal alike — because it is
 * a machine key, not user data: rendered, it reads "Certification › Certification
 * And License Integration Guid: '9f3c…' → —", putting a raw GUID on screen. It is
 * written purely so a removed certification can still be named
 * (see `buildEntryMemberNameResolver`), then consumed and dropped.
 */
const CERTIFICATION_CATALOG_KEY_SUFFIX =
  '.CertificationAndLicenseIntegrationGuid';

// The full path of that key row, capturing the certification's per-resource row
// id — the same id every other field row for that certification is keyed by.
const CERTIFICATION_CATALOG_KEY_PATH =
  /^SelectedCertificationsAndLicenses\.\[([^\]]+)\]\.CertificationAndLicenseIntegrationGuid$/;

/**
 * A field row carrying a collection item's own name, capturing the collection and id
 * it belongs to. Anchored so the DEEPEST `<Collection>.[<id>]` wins: a nested
 * `SelectedAttributeDefinitions.[a].SelectedAttributeValuesVarchars.[b].DisplayName`
 * names the value `b`, not the definition `a`.
 */
const ITEM_NAME_PATH = /([A-Za-z]+)\.\[([^\]]+)\]\.(?:Name|DisplayName)$/;

// Composite map key. `::` cannot be ambiguous here: a collection segment is matched as
// `[A-Za-z]+`, so it never contains a colon, which fixes where the split falls.
const identityKey = (collection: string, memberId: string) =>
  `${collection}::${memberId.toLowerCase()}`;

/**
 * True for a field change that exists only to carry a machine identifier and must
 * never be rendered, in the grid summary OR the detail modal.
 */
export function isIdentityKeyFieldChange(
  change: ResourceHistoryFieldChange
): boolean {
  return change.fieldPath.endsWith(CERTIFICATION_CATALOG_KEY_SUFFIX);
}

/**
 * Field changes that should be rendered at all. Strips the machine-key rows from
 * whatever surface is displaying them.
 */
export function visibleFieldChanges(
  // `null` as well as `undefined`: the API sends an explicit null for action-level
  // (schedule/event) rows and for resource rows with no parsed detail, so
  // ResourceHistoryEntry.fieldChanges is nullable, not merely optional.
  changes: readonly ResourceHistoryFieldChange[] | null | undefined
): ResourceHistoryFieldChange[] {
  return (changes ?? []).filter((change) => !isIdentityKeyFieldChange(change));
}

/**
 * Wraps a resolver so items REMOVED from the resource can still be named.
 *
 * The base resolver names an item by looking its GUID up in the resource's CURRENT
 * collections, which by definition cannot work for something deleted. Two per-entry
 * fallbacks close that, both reading the audit entry itself:
 *
 * 1. Any collection — the builder writes an added/removed item's own fields, so the
 *    item's `Name`/`DisplayName` row is right there in the entry. Use it (the
 *    `oldValue` on a delete, the `newValue` on a create).
 * 2. Certifications — the exception, because a certification entry has no name
 *    anywhere. It carries only a catalog key, so that key is resolved against the
 *    company catalog instead.
 *
 * The base resolver is consulted FIRST, so a still-present item keeps the resource's
 * current name: if the item was renamed in this very entry, the grid shows what it is
 * called now rather than what it was called then.
 */
export function buildEntryMemberNameResolver(
  // Nullable for the same reason as visibleFieldChanges above.
  changes: readonly ResourceHistoryFieldChange[] | null | undefined,
  baseResolve: MemberNameResolver,
  certificationNameByCatalogGuid: Map<string, string>
): MemberNameResolver {
  const nameFromAuditDetail = new Map<string, string>();
  const certificationNameByRowGuid = new Map<string, string>();

  for (const change of changes ?? []) {
    // A delete carries the surviving value in oldValue, a create in newValue.
    const recordedValue = change.oldValue || change.newValue;
    if (!recordedValue) {
      continue;
    }

    const nameMatch = ITEM_NAME_PATH.exec(change.fieldPath);
    if (nameMatch) {
      nameFromAuditDetail.set(
        identityKey(nameMatch[1], nameMatch[2]),
        recordedValue
      );
      continue;
    }

    const catalogMatch = CERTIFICATION_CATALOG_KEY_PATH.exec(change.fieldPath);
    if (catalogMatch) {
      const name = certificationNameByCatalogGuid.get(
        recordedValue.toLowerCase()
      );
      if (name) {
        certificationNameByRowGuid.set(catalogMatch[1].toLowerCase(), name);
      }
    }
  }

  return (collection, memberId) =>
    baseResolve(collection, memberId) ??
    nameFromAuditDetail.get(identityKey(collection, memberId)) ??
    (collection === 'SelectedCertificationsAndLicenses'
      ? certificationNameByRowGuid.get(memberId.toLowerCase())
      : undefined);
}

/**
 * Actor rendering is presentation-only — the display name is resolved
 * server-side, so we never do a client name lookup.
 */
export function renderActor(actor: ResourceHistoryEntry['actor']): string {
  switch (actor.type) {
    case 'bulk':
      return 'Bulk Update';
    case 'system':
      return 'System';
    case 'user':
    default:
      return actor.displayName ?? 'Unknown';
  }
}

/**
 * True for a field change hidden from the grid summary only. Covers both the
 * auto-managed timestamps and the low-signal leaves above — the latter are real
 * user data, not system fields, which is why this is named for the SURFACE that
 * suppresses them rather than for a property of the field.
 */
export function isSummarySuppressedFieldChange(
  change: ResourceHistoryFieldChange
): boolean {
  if (
    SYSTEM_FIELD_SUFFIXES.some((suffix) => change.fieldPath.endsWith(suffix))
  ) {
    return true;
  }

  const leafField = change.fieldPath.split('.').pop() ?? '';
  return SUMMARY_NOISE_LEAF_FIELDS.includes(leafField);
}

export function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return timestamp;
  }
  return `${date.toLocaleString()} (UTC${utcOffset()})`;
}

/**
 * Human-readable one-line summary for the grid Detail column. Auto timestamp
 * fields are always suppressed here to keep the row scannable; the full,
 * unfiltered field list is shown in the row-detail modal. Runs each change
 * through the shared `formatChangeCells` with the same resolver the modal uses,
 * so the grid and modal agree on member names (e.g. "Downtown Branch (created)").
 *
 * Rows are grouped by owning item via the same `groupFieldChanges` the modal
 * uses, so an item is named ONCE. Naming it per row instead turned a deleted
 * location into ~600 characters of "Location (Home Office) › " repeated a dozen
 * times, which is harder to scan than the anonymous rows this all replaced.
 */
export function buildSummaryDetail(
  entry: ResourceHistoryEntry,
  resolveName: MemberNameResolver
): string {
  const visible = visibleFieldChanges(entry.fieldChanges);
  const changes = visible.filter(
    (change) => !isSummarySuppressedFieldChange(change)
  );

  if (changes.length === 0) {
    // Distinguish "every row here was suppressed as noise" from "the API sent no
    // parsed detail at all". Toggling only a location's day availability, or a
    // system re-geocode, otherwise reads exactly like an entry with no field rows.
    return visible.length > 0
      ? `${entry.actionType} — see details`
      : entry.actionType;
  }

  return groupFieldChanges(changes, resolveName)
    .map((group) => renderSummaryGroup(group, resolveName))
    .join('; ');
}

/**
 * One group as a fragment of the one-line summary: `"<heading> › field: old → new,
 * field: old → new"`, naming the item once in the heading instead of on every row.
 *
 * ` › ` rather than `: ` between heading and fields so a single-field change still
 * reads as one continuous path ("Location (Home Office) › Street: …") instead of
 * doubling up the colon.
 */
function renderSummaryGroup(
  group: FieldChangeGroup,
  resolveName: MemberNameResolver
): string {
  // Each row keeping its own full path — how every row rendered before grouping,
  // and still never a raw GUID for an item that will not resolve.
  const renderFlat = () =>
    group.changes
      .map((change) => {
        const { oldValue, newValue } = formatChangeCells(change, resolveName);
        const label = formatFieldPath(change.fieldPath, resolveName);
        return `${label}: ${oldValue} → ${newValue}`;
      })
      .join('; ');

  // No heading means either an ungrouped row or an item that would not resolve.
  if (!group.heading) {
    return renderFlat();
  }

  const rows = group.changes.map((change) => ({
    label: formatPathWithinItem(group, change.fieldPath, resolveName),
    ...formatChangeCells(change, resolveName),
  }));

  // A group that is ONLY the membership row has no fields to head, and its value
  // cell already reads "Downtown Branch (created)" — so a heading here would name
  // the item twice. This is the same reason `formatFieldPath` skips a leaf index.
  if (rows.every((row) => !row.label)) {
    return renderFlat();
  }

  const parts = rows.map((row) =>
    row.label
      ? `${row.label}: ${row.oldValue} → ${row.newValue}`
      : `${row.oldValue} → ${row.newValue}`
  );

  return `${group.heading} › ${parts.join(', ')}`;
}
